import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  /** Right-side slide-over width (CSS value). Default 480px. */
  width?: string;
}

/**
 * Right-side slide-over panel. Drill-down container so we don't navigate to
 * a new route — keeps the SPA snappy and preserves tab + search context.
 *
 * Tailwind-only (no shadcn dep). Backdrop click + Escape closes. Animated
 * via translate-x; uses `transition-transform`.
 */
export const Sheet: React.FC<Props> = ({ open, onClose, title, subtitle, children, width = '480px' }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[500] transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/50 dark:bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
        role="presentation"
      />
      {/* Panel */}
      <aside
        data-testid="sheet-panel"
        className={`absolute top-0 right-0 h-full bg-white dark:bg-ink text-ink dark:text-white shadow-[-20px_0_40px_-10px_rgba(0,0,0,0.25)] transition-transform duration-300 ease-out flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ width: `min(${width}, 100vw)` }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-black/8 dark:border-white/10">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold tracking-[-0.01em] truncate">{title}</h2>}
            {subtitle && <p className="text-xs text-ink/55 dark:text-white/55 mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 text-ink/55 dark:text-white/55 transition-colors flex-shrink-0"
            aria-label="Sluit"
          >
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
};
